/**
 * Client form validation schema
 * Validates client data with country-specific rules
 */

import * as Yup from 'yup'
import { FORM_LABELS, LABELS } from '@constants'
import {
  createNLPostalCodeValidator,
  createNLKVKValidator,
  createNLBTWIDValidator,
  createEmailValidator,
} from '@utilities/validation'

// Explicit type for client form data
export interface IClientFormData {
  company_id: string
  country: {
    id: number
    name: string
    code: string
  }
  client_type: 'particulier' | 'zakelijk'
  name: string
  code: string
  email: string
  street: string
  house_number: string
  house_number_addition: string
  postal_code: string
  city: string
  coc: string
  tax: string
  hourly_rate: number | null
  created_at: null
  updated_at: null
  deleted_at: null
  contacts: never[]
}

/**
 * Client validation schema with dynamic country-based validation
 * - Netherlands (NL): Strict validation for postal code, KVK, and BTW-ID
 * - Other countries: Basic format validation
 */
export const clientValidationSchema = () =>
  Yup.object().shape({
    company_id: Yup.string().required(FORM_LABELS.REQUIRED(LABELS.COMPANY)),

    client_type: Yup.string().oneOf(['particulier', 'zakelijk']).default('zakelijk'),

    country: Yup.object()
      .shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        code: Yup.string().required(),
      })
      .required(FORM_LABELS.REQUIRED(LABELS.COUNTRY)),

    name: Yup.string()
      .required(FORM_LABELS.REQUIRED(LABELS.CLIENT_NAME))
      .max(255, FORM_LABELS.MAX_LENGTH(LABELS.CLIENT_NAME, 255)),

    code: Yup.string()
      .nullable()
      .default('')
      .max(255, FORM_LABELS.MAX_LENGTH(LABELS.CLIENT_CODE, 255))
      .when('client_type', {
        is: 'particulier',
        then: (schema) => schema.strip(),
        otherwise: (schema) => schema,
      }),

    email: createEmailValidator(FORM_LABELS.INVALID_EMAIL)
      .nullable()
      .default('')
      .max(255, FORM_LABELS.MAX_LENGTH(LABELS.EMAIL, 255)),

    street: Yup.string().required(FORM_LABELS.REQUIRED(LABELS.STREET)),

    house_number: Yup.string()
      .required(FORM_LABELS.REQUIRED(LABELS.HOUSE_NUMBER))
      .max(20, FORM_LABELS.MAX_LENGTH(LABELS.HOUSE_NUMBER, 20)),

    house_number_addition: Yup.string()
      .nullable()
      .max(20, FORM_LABELS.MAX_LENGTH(LABELS.HOUSE_NUMBER_ADDITION, 20)),

    // Postal code validation - conditional based on country
    postal_code: Yup.string()
      .required(FORM_LABELS.REQUIRED(LABELS.POSTAL_CODE))
      .when('country', {
        is: (country: any) => country?.code === 'NL',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        then: (_schema) => createNLPostalCodeValidator(FORM_LABELS.INVALID_POSTAL_CODE_NL),
        otherwise: (schema) => schema.max(20, FORM_LABELS.MAX_LENGTH(LABELS.POSTAL_CODE, 20)),
      }),

    city: Yup.string().required(FORM_LABELS.REQUIRED(LABELS.CITY)),

    // KVK number validation
    // - particulier: strip (individuals have no CoC number)
    // - zakelijk + NL: strict 8-digit KVK validation
    // - zakelijk + non-NL: keep free-form (foreign CoC/Handelsregister/Companies House/etc.)
    coc: Yup.string()
      .nullable()
      .default('')
      .when('client_type', {
        is: 'particulier',
        then: (schema) => schema.strip(),
        otherwise: (schema) =>
          schema.when('country', {
            is: (country: any) => country?.code === 'NL',
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            then: (_s) => createNLKVKValidator(FORM_LABELS.INVALID_KVK_NUMBER),
            otherwise: (s) => s,
          }),
      }),

    // BTW-ID/VAT validation
    // - particulier: strip (individuals have no VAT number)
    // - zakelijk + NL: strict NL BTW-ID format
    // - zakelijk + non-NL: keep free-form (foreign VAT formats vary per country)
    tax: Yup.string()
      .nullable()
      .default('')
      .when('client_type', {
        is: 'particulier',
        then: (schema) => schema.strip(),
        otherwise: (schema) =>
          schema.when('country', {
            is: (country: any) => country?.code === 'NL',
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            then: (_s) => createNLBTWIDValidator(FORM_LABELS.INVALID_BTW_ID_NL),
            otherwise: (s) => s,
          }),
      }),

    hourly_rate: Yup.number()
      .nullable()
      .min(0, FORM_LABELS.HOURLY_RATE_NEGATIVE)
      .integer(FORM_LABELS.HOURLY_RATE_INTEGER)
      .when('client_type', {
        is: 'particulier',
        then: (schema) => schema.strip(),
        otherwise: (schema) => schema,
      }),
  })
